<script setup lang="ts">
    import $ from "jquery"
    import { computed, watch, onMounted, onUnmounted, nextTick, ref, type Ref } from "vue"
    import { useRoute } from "vue-router"

    import CorporateNavigation from "@common/Components/CorporateNavigation.vue"
    import ScenarioNavigation from "@Quest/CorporateScenarios/Components/ScenarioNavigation.vue"

    import { useSchedulingPageQueryV1 } from "@Quest/CorporateScenarios/queries-vue-composables"

    import type {
        ScenarioItemReadModel,
        SchedulingPageReadModel
    } from "@Quest/CorporateScenarios/readmodels"


    // ======================
    // Route
    // ======================

    const route = useRoute()

    const workspaceId = computed(() => Number(route.params.workspaceId ?? 0))
    const strategyId = computed(() => String(route.params.strategyId ?? ""))
    const scenarioId = computed(() => String(route.params.scenarioId ?? ""))


    // ======================
    // Query
    // ======================

    const {
        runSchedulingPageQueryV1,
        modelSchedulingPageQueryV1
    } = useSchedulingPageQueryV1(workspaceId, strategyId, scenarioId)

    const rm = modelSchedulingPageQueryV1 as Ref<SchedulingPageReadModel | null>

    // ======================
    // Local State
    // ======================

    const searchPhrase = ref<string | null>(null)


    // ======================
    // Computed
    // ======================

    const projectsView = computed<ScenarioItemReadModel[]>(() =>
    {
        const items = rm.value?.Items ?? []
        let view = [...items]

        if (searchPhrase.value)
        {
            const phrase = searchPhrase.value.toLowerCase()
            view = view.filter(a =>
                a.Name.toLowerCase().indexOf(phrase) > -1
            )
        }

        return view
    })

    const ds = computed(() =>
    {
        if (!rm.value) return []

        return projectsView.value
            .filter(a => a.StartDate && a.EndDate)
            .map(a => ({
                id: a.Id,
                title: a.Name,
                start: new Date(a.StartDate!),
                end: new Date(a.EndDate!)
            }))
    })


    // ======================
    // Gantt Handling
    // ======================

    const ganttRef = ref<HTMLElement | null>(null)

    function edit(e: any)
    {
        const selection = e.sender.select()
        const task = e.sender.dataItem(selection)
    }

    function ensureGantt()
    {
        const el = ganttRef.value
        if (!el) return

        const existing = ($(el) as any).data("kendoGantt")

        if (existing)
        {
            existing.setDataSource(
                new kendo.data.GanttDataSource({ data: ds.value })
            )
            return
        }

        ($(el) as any).kendoGantt({
            editable: false,
            height: "100%",
            tooltip: { visible: false },
            views: [{ type: "year", selected: true }],
            columns: [
                { field: "title", title: "Name", sortable: true },
                { field: "start", title: "Start", format: "{0:MM/yyyy}", sortable: true, width: 120 },
                { field: "end", title: "End", format: "{0:MM/yyyy}", sortable: true, width: 120 }
            ],
            dataSource: ds.value,
            change: (e: any) => edit(e)
        })
    }


    // ======================
    // Watchers
    // ======================

    watch(rm, async (current, previous) =>
    {
        if (current && !previous)
        {
            await nextTick()
            ensureGantt()
        }
    })

    watch(ds, () =>
    {
        nextTick(() => ensureGantt())
    })


    function onScheduled(result: any)
    {
        runSchedulingPageQueryV1()
    }


    // ======================
    // Lifecycle
    // ======================

    onMounted(() =>
    {
        document.title = "Enterprise Plan - Schedule"
        runSchedulingPageQueryV1()
    })

    onUnmounted(() =>
    {
        const el = ganttRef.value
        const gantt = ($(el) as any)?.data("kendoGantt")
        if (gantt) gantt.destroy()
    })
</script>

<template>
    <div>
        <CorporateNavigation ActivePage="dashboard"></CorporateNavigation>

        <div v-if="rm" class="container-fluid" style="margin-top: 80px; padding-top: 20px;">
            <div class="row">
                <div class="col-lg-6">
                    <small class="text-muted">Enterprise Plan: {{rm.ScenarioName}}</small>
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b; margin: 0;">Schedule</h1>
                </div>
                <div class="col-lg-6 d-flex flex-column justify-content-end">
                    <ScenarioNavigation @scheduled="onScheduled" />
                </div>
            </div>

            <hr />

            <nav class="mt-5 navbar navbar-light navbar-expand-md" style="background-color: rgba(0, 0, 0, 0.03); border: 1px solid rgba(0, 0, 0, 0.125); border-bottom: 0; " role="navigation">
                <form class="form-inline my-2 my-lg-0 ml-auto" role="search">
                    <input class="form-control mr-sm-2" type="search" placeholder="Search" v-model="searchPhrase">
                </form>
            </nav>

            <div style="height: calc(100vh - 300px);">
                <div ref="ganttRef" style="height:100%"></div>
            </div>
        </div>
    </div>
</template>
